import React from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { loanService } from '../../services/loanService';
import { formatCurrency, formatDate } from '../../utils/formatters';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const LoanApproval = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery(
    'pending-loans',
    () => loanService.getPendingLoans(),
    { refetchInterval: 30000 }
  );

  const approveMutation = useMutation(
    (loanId) => loanService.approveLoan(loanId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('pending-loans');
        toast.success('Loan approved successfully');
      }
    }
  );

  const disburseMutation = useMutation(
    (loanId) => loanService.disburseLoan(loanId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('pending-loans');
        toast.success('Loan disbursed successfully');
      }
    }
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const pendingLoans = data?.data || [];

  if (pendingLoans.length === 0) {
    return (
      <div className="p-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-semibold text-green-800 mb-2">No Pending Loans</h3>
          <p className="text-green-600">All loan applications have been processed.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Loan Approval Dashboard</h1>

      <div className="space-y-4">
        {pendingLoans.map((loan) => (
          <div key={loan.id} className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold">{loan.loan_no}</h3>
                <p className="text-gray-600">Member: {loan.member_name} ({loan.member_code})</p>
                <p className="text-gray-600">Phone: {loan.phone}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-500">Application Date</p>
                <p className="font-medium">{formatDate(loan.application_date)}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <p className="text-sm text-gray-500">Loan Amount</p>
                <p className="text-lg font-bold text-blue-600">{formatCurrency(loan.principal_amount)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Interest Rate</p>
                <p className="text-lg font-bold">{loan.interest_rate}%</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Duration</p>
                <p className="text-lg font-bold">{loan.duration_months} months</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Monthly EMI</p>
                <p className="text-lg font-bold text-green-600">{formatCurrency(loan.emi_amount)}</p>
              </div>
            </div>

            {loan.purpose && (
              <div className="mb-4">
                <p className="text-sm text-gray-500">Purpose</p>
                <p>{loan.purpose}</p>
              </div>
            )}

            {loan.guarantor_name && (
              <div className="mb-4">
                <p className="text-sm text-gray-500">Guarantor</p>
                <p>{loan.guarantor_name} {loan.guarantor_phone && `(${loan.guarantor_phone})`}</p>
              </div>
            )}

            <div className="flex justify-end space-x-3 pt-4 border-t">
              {loan.status === 'pending' && (
                <button
                  onClick={() => approveMutation.mutate(loan.id)}
                  disabled={approveMutation.isLoading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Approve Loan
                </button>
              )}
              {loan.status === 'approved' && (
                <button
                  onClick={() => disburseMutation.mutate(loan.id)}
                  disabled={disburseMutation.isLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Disburse Loan
                </button>
              )}
              <button
                onClick={() => navigate(`/loans/${loan.id}`)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-50"
              >
                View Details
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default LoanApproval;